<template>
  <div class="block-employee-list section">
    <div class="container">
      <ul
        class="flex gap-2 tablet:flex-wrap mb-6 overflow-auto tablet:overflow-hidden"
      >
        <li
          v-for="tab in tabs"
          :key="tab.id"
          :class="{ 'is-active': activeDepartment === tab.id }"
        >
          <a
            :href="'#' + tab.id"
            class="inline-block border border-solid border-muted text-muted font-bold rounded-full whitespace-nowrap py-1 px-3 text-size-6"
            :class="{
              'bg-primary text-scheme-main border-primary':
                tab.id === activeDepartment,
              'hover:bg-scheme-main-bis': tab.id !== activeDepartment,
            }"
            @click.prevent="onTabClick(tab.id)"
            >{{ tab.label }}</a
          >
        </li>
      </ul>
      <div class="employee-list">
        <ul>
          <li v-for="item in employees" :key="item.node.nid">
            <EmployeeTeaser v-bind="item.node" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'
import { onlyUnique } from '~/helpers'

defineBlokkliFragment({
  name: 'team_list',
  label: 'Team List',
  chunkName: 'rare',
  editor: {
    noPreview: true,
  },
})

const activeDepartment = useQueryString('department', 'all')

function onTabClick(id: string) {
  activeDepartment.value = id
}

const { activeDomain } = useSiteContext()
const language = useCurrentLanguage()
const { data } = await useAsyncGraphqlQuery(
  'employees',
  {
    domain: activeDomain,
  },
  {
    transform: function (data) {
      const departments = data.data.departments.items?.reduce<
        Record<string, string>
      >((acc, v) => {
        if (v && 'name' in v && v.tid && v.name) {
          acc[v.tid] = v.name
        }

        return acc
      }, {})

      const employees =
        data.data.employees.items
          ?.map((node) => {
            if (node && 'title' in node) {
              const departments = (node.department || [])
                .map((v) => {
                  if (v && v.id) {
                    return v.id
                  }

                  return null
                })
                .filter(notNullish)
              return { node, departments }
            }
          })
          .filter(notNullish) || []

      return {
        departments,
        employees,
      }
    },
    watch: [language],
  },
)

const availableDepartments = computed<string[]>(
  () =>
    data.value?.employees
      .flatMap((v) => v.departments)
      .filter(notNullish)
      .filter(onlyUnique) || [],
)

const { $texts } = useEasyTexts()

const tabs = computed(() => {
  const items = availableDepartments.value.map((id) => {
    return {
      id,
      label: data.value?.departments?.[id],
    }
  })

  return [
    {
      id: 'all',
      label: $texts('all', 'All'),
    },
    ...items,
  ]
})

const employees = computed(() => {
  const items = data.value?.employees || []
  if (activeDepartment.value === 'all') {
    return items
  }

  return items.filter((v) => v.departments.includes(activeDepartment.value))
})

await renderPageDependencies()
</script>

<style lang="postcss">
.employee-list {
  > ul {
    @apply grid;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 0.5rem;
    @screen tablet {
      grid-template-columns: repeat(3, 1fr);
      grid-gap: theme('spacing.size-4') theme('spacing.size-6');
    }
    @screen desktop {
      grid-template-columns: repeat(3, 1fr);
      grid-gap: theme('spacing.size-2') theme('spacing.size-6');
    }
  }
}

.employee-teaser-links {
  @apply tablet:flex tablet:flex-wrap;
  @screen tablet {
    > a {
      margin-right: 0.5em;
    }
  }
}
</style>
